import { useEffect, useState } from "react"
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Drawer,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material"
import { Close as CloseIcon } from "@mui/icons-material"
import { useGetIntersectionMembersQuery } from "../../store/chatAnalyzeApi"
import type { ChatAnalyze } from "../interfaces"

interface IntersectionDrawerProps {
  open: boolean
  chatA: { id: string; name: string }
  chatB: { id: string; name: string }
  totalCommon: number
  onClose: () => void
}

const PAGE_SIZE = 50

export function IntersectionDrawer({
  open,
  chatA,
  chatB,
  totalCommon,
  onClose,
}: IntersectionDrawerProps) {
  const [page, setPage] = useState(1)
  const [accumulated, setAccumulated] = useState<
    ChatAnalyze.IntersectionMember[]
  >([])

  // Reset state when the drawer is opened for a new pair
  useEffect(() => {
    if (open) {
      setPage(1)
      setAccumulated([])
    }
  }, [open, chatA.id, chatB.id])

  const { data, isLoading, isFetching, isError, refetch } =
    useGetIntersectionMembersQuery(
      { chatIds: [chatA.id, chatB.id], page, limit: PAGE_SIZE },
      { skip: !open },
    )

  // Append new page items to the accumulated list
  useEffect(() => {
    if (!data?.items) return
    setAccumulated((prev) => {
      const knownIds = new Set(prev.map((m) => m.id))
      const additions = data.items.filter((m) => !knownIds.has(m.id))
      return additions.length ? [...prev, ...additions] : prev
    })
  }, [data])

  const hasMore = data ? accumulated.length < data.total : false

  const handleClose = () => {
    onClose()
  }

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={handleClose}
      PaperProps={{ sx: { width: { xs: "100%", sm: 480 } } }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
        {/* Header */}
        <Stack
          direction="row"
          alignItems="center"
          sx={{
            px: 2,
            py: 1.5,
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          <Typography variant="h6" sx={{ flex: 1 }} noWrap>
            {chatA.name} ∩ {chatB.name}
          </Typography>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </Stack>

        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ px: 2, pt: 1 }}
        >
          Всего общих участников:{" "}
          <strong>{totalCommon.toLocaleString("ru-RU")}</strong>
        </Typography>

        {/* Content */}
        <Box sx={{ flex: 1, overflow: "auto", px: 2, py: 1 }}>
          {isError && (
            <Alert
              severity="error"
              action={
                <Button size="small" onClick={() => refetch()}>
                  Повторить
                </Button>
              }
            >
              Ошибка загрузки
            </Alert>
          )}

          {accumulated.length > 0 && (
            <TableContainer>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>#</TableCell>
                    <TableCell>Имя</TableCell>
                    <TableCell>ID</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {accumulated.map((m, idx) => (
                    <TableRow
                      key={m.id}
                      sx={{ "&:last-child td": { border: 0 } }}
                    >
                      <TableCell>{idx + 1}</TableCell>
                      <TableCell>
                        {m.isBot ? "🤖 " : ""}
                        {m.name}
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" color="text.secondary">
                          {m.tgUserId}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {(isLoading || isFetching) && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
              <CircularProgress size={28} />
            </Box>
          )}

          {hasMore && !isFetching && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
              <Button
                variant="outlined"
                size="small"
                onClick={() => setPage((p) => p + 1)}
              >
                Загрузить ещё
              </Button>
            </Box>
          )}

          {!isLoading && !isFetching && !hasMore && accumulated.length > 0 && (
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
              align="center"
              sx={{ py: 1 }}
            >
              Показаны все {accumulated.length} участников
            </Typography>
          )}
        </Box>
      </Box>
    </Drawer>
  )
}
